import React, { lazy, Suspense } from 'react'
import * as Sentry from '@sentry/react'
import { useNavLayout } from './hooks/useNavLayout'
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth, type Permissoes }  from './hooks/useAuth'
import { ClienteAuthProvider, useClienteAuth }    from './hooks/useClienteAuth.jsx'
import { TooltipProvider }                        from './components/ui/tooltip'
import { MeetingProvider, useMeeting }                    from './contexts/MeetingContext'
import { SessaoCapturaProvider, useSessaoCapturaCtx }     from './contexts/SessaoCapturaContext'
import { TimeTrackerProvider }                            from './contexts/TimeTrackerContext'
import { EscritorioProvider }                            from './contexts/EscritorioContext'
import BoasVindas      from './components/projetos/BoasVindas'
import ReuniaoAoVivo   from './components/projetos/ReuniaoAoVivo'
import MeetingCompact  from './components/projetos/MeetingCompact'
import MeetingBar      from './components/projetos/MeetingBar'
import SessaoCaptura  from './components/captura/SessaoCaptura'
import CapturaBar     from './components/captura/CapturaBar'
import AppLayout      from './components/layout/AppLayout'
import Login          from './pages/Login'

// Lazy-loaded — code-splitting por rota
const Dashboard      = lazy(() => import('./pages/Dashboard'))
const MobileHome     = lazy(() => import('./pages/MobileHome'))
const Projetos       = lazy(() => import('./pages/Projetos'))
const Clientes       = lazy(() => import('./pages/Clientes'))
const ClienteDetalhe = lazy(() => import('./pages/clientes/ClienteDetalhe'))
const ProjetoDetalhe = lazy(() => import('./pages/ProjetoDetalhe'))
const EtapaDetalhe   = lazy(() => import('./pages/EtapaDetalhe'))
const Tarefas        = lazy(() => import('./pages/Tarefas'))
const Agenda         = lazy(() => import('./pages/Agenda'))
const Notas          = lazy(() => import('./pages/Notas'))
const LoginCliente      = lazy(() => import('./pages/cliente/LoginCliente'))
const DefinirSenhaCliente = lazy(() => import('./pages/cliente/DefinirSenhaCliente'))
const DashboardCliente  = lazy(() => import('./pages/cliente/DashboardCliente'))
const ProjetoCliente    = lazy(() => import('./pages/cliente/ProjetoCliente'))
const PublicProjetoView   = lazy(() => import('./pages/PublicProjetoView'))
const PublicPropostaView  = lazy(() => import('./pages/public/PublicPropostaView'))
const PublicRevisaoView   = lazy(() => import('./pages/public/PublicRevisaoView'))
const PublicAtaView       = lazy(() => import('./pages/public/PublicAtaView'))
const PublicApresentacaoView = lazy(() => import('./pages/public/PublicApresentacaoView'))
const ApresentacaoCliente    = lazy(() => import('./pages/cliente/ApresentacaoCliente'))
const Showroom          = lazy(() => import('./pages/Showroom'))
const LandingYachtClub  = lazy(() => import('./pages/public/landing/LandingYachtClub'))
const LandingBaciaVovo  = lazy(() => import('./pages/public/landing/LandingBaciaVovo'))
const BdvMasterplan     = lazy(() => import('./pages/public/landing/BdvMasterplan'))
const LandingMagnifique = lazy(() => import('./pages/public/landing/LandingMagnifique'))
const LandingTN03       = lazy(() => import('./pages/public/landing/LandingTN03'))
const SiteHome          = lazy(() => import('./site/pages/Home'))
const LandingCidade     = lazy(() => import('./site/pages/LandingCidade'))
const ProjetoPagina     = lazy(() => import('./site/pages/ProjetoPagina'))
const Contato           = lazy(() => import('./site/pages/Contato'))
const ArtigosIndex      = lazy(() => import('./site/pages/ArtigosIndex'))
const ArtigoBIM         = lazy(() => import('./site/pages/ArtigoBIM'))
const ArtigoInstalacoes = lazy(() => import('./site/pages/ArtigoInstalacoes'))
const Branding         = lazy(() => import('./site/pages/Branding'))
const PortfolioRestrito = lazy(() => import('./site/pages/PortfolioRestrito'))
const PortfolioSerraAlta = lazy(() => import('./site/pages/PortfolioSerraAlta'))
const SiteNotFound      = lazy(() => import('./site/pages/NotFound'))
const AppsHome          = lazy(() => import('./pages/AppsHome'))
const Propostas         = lazy(() => import('./pages/Propostas'))
const PropostaViewer    = lazy(() => import('./pages/PropostaViewer'))
const PropostaBuilder   = lazy(() => import('./pages/PropostaBuilder'))
const PropostaPreview   = lazy(() => import('./pages/PropostaPreview'))
const Catalogos         = lazy(() => import('./pages/Catalogos'))
const Configuracoes     = lazy(() => import('./pages/Configuracoes'))
const Financeiro        = lazy(() => import('./pages/Financeiro'))
const CadastroLead         = lazy(() => import('./pages/public/CadastroLead'))
const CadastroConfirmacao  = lazy(() => import('./pages/public/CadastroConfirmacao'))
const AtualizarCadastro    = lazy(() => import('./pages/public/AtualizarCadastro'))
const BemVindo             = lazy(() => import('./pages/public/BemVindo'))
const ChecklistPublico     = lazy(() => import('./pages/public/ChecklistPublico'))
const AceitarConvite       = lazy(() => import('./pages/AceitarConvite'))
const Compartilhar         = lazy(() => import('./pages/Compartilhar'))

// ─── Spinner inline ────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)' }}>
      <span className="material-symbols-outlined text-2xl" style={{ color: 'var(--color-muted)' }}>
        progress_activity
      </span>
    </div>
  )
}

// ─── Rota escritório: redireciona clientes para /cliente/dashboard ─────────────
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading }                        = useAuth()
  const { portalUser, loading: clienteLoading }  = useClienteAuth()
  // Subdomínios públicos (apresentacao.*, proposta.*) NUNCA expõem a área
  // restrita, mesmo com sessão do escritório no mesmo browser (ADR-015).
  const mode = getHostMode()
  if (mode === 'apresentacao' || mode === 'proposta') return <PublicNotFound />
  if (loading || clienteLoading) return <Spinner />
  if (!user) return <Navigate to="/login" replace />
  if (portalUser) return <Navigate to="/cliente/dashboard" replace />
  return children
}

// 404 público — usado em subdomínios que não devem mostrar o portal
function PublicNotFound() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#080808] text-[#f0f0f0]">
      <div className="text-center px-8">
        <p className="text-[12px] tracking-[0.12em] uppercase text-[#888] font-bold">IFP Arquitetura</p>
        <h1 className="text-[22px] font-extrabold mt-3 mb-1.5">Página não encontrada</h1>
        <p className="text-[13px] text-[#999]">O link pode estar incorreto ou expirado.</p>
      </div>
    </div>
  )
}

// Index / catch-all do portal: redireciona pra /hoje no portal, mas mostra 404
// público nos subdomínios públicos (não vaza pro dashboard).
function PortalIndexOrNotFound() {
  const mode = getHostMode()
  if (mode === 'apresentacao' || mode === 'proposta') return <PublicNotFound />
  return <Navigate to="/hoje" replace />
}

// ─── Rota cliente: exige portal_user ──────────────────────────────────────────
function ClienteRoute({ children }: { children: React.ReactNode }) {
  const { user, loading }                        = useAuth()
  const { portalUser, loading: clienteLoading }  = useClienteAuth()
  if (loading || clienteLoading) return <Spinner />
  if (!user) return <Navigate to="/cliente/login" replace />
  if (!portalUser) return <Navigate to="/cliente/login" replace />
  return children
}

// ─── Rota com permissão por módulo ────────────────────────────────────────────
function PermissionRoute({ modulo, children }: { modulo: keyof Omit<Permissoes, 'projetos'>; children: React.ReactNode }) {
  const { user, loading, can } = useAuth()
  if (loading) return <Spinner />
  if (!user) return <Navigate to="/login" replace />
  if (!can(modulo)) return <Navigate to="/hoje" replace />
  return children
}

// ─── Rota Showroom: só admin e financeiro ──────────────────────────────────────
function ShowroomRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, canShowroom } = useAuth()
  if (loading) return <Spinner />
  if (!user) return <Navigate to="/login" replace />
  if (!canShowroom) return <Navigate to="/hoje" replace />
  return children
}

function PreviewDashboardWrapper() {
  const { clienteId } = useParams()
  const { role, loading } = useAuth()
  if (loading) return <Spinner />
  if (!role) return <Navigate to="/hoje" replace />
  return <DashboardCliente previewClienteId={Number(clienteId)} />
}

function PreviewProjetoWrapper() {
  const { role, loading } = useAuth()
  if (loading) return <Spinner />
  if (!role) return <Navigate to="/hoje" replace />
  return <ProjetoCliente previewMode={true} />
}

// ─── /hoje — Dashboard no desktop, MobileHome no mobile ──────────────────────
function HojeRoute() {
  const mode = useNavLayout()
  return mode === 'mobile' ? <MobileHome /> : <Dashboard />
}

// ─── Placeholder para rotas em desenvolvimento ────────────────────────────────
function Placeholder({ titulo }: { titulo: string }) {
  return (
    <div style={{ display: 'flex', height: '100%', width: '100%', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <span className="material-symbols-outlined text-[32px]" style={{ color: '#7A7A7A' }}>construction</span>
      <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#7A7A7A' }}>
        {titulo} — em desenvolvimento
      </p>
    </div>
  )
}

// ─── Host-aware routing (ADR-016) ─────────────────────────────────────────────
// Produção divide o domínio em três hosts:
//   - ifparquitetura.com / www.* → site institucional (SiteHome + públicos)
//   - portal.ifparquitetura.com  → área restrita do escritório
//   - cliente.ifparquitetura.com → portal do cliente
// Dev local, preview Vercel e subdomínios apresentacao.*/proposta.* mostram tudo
type HostMode = 'root' | 'portal' | 'cliente' | 'apresentacao' | 'proposta' | 'full'

function getHostMode(): HostMode {
  if (typeof window === 'undefined') return 'full'
  // Escape-hatch de dev: ?as=root|portal|cliente|full força o modo
  const override = new URLSearchParams(window.location.search).get('as')
  if (['root', 'portal', 'cliente', 'apresentacao', 'proposta', 'full'].includes(override ?? '')) {
    return override as HostMode
  }
  const h = window.location.hostname.toLowerCase()
  if (h === 'ifparquitetura.com' || h === 'www.ifparquitetura.com') return 'root'
  if (h === 'portal.ifparquitetura.com') return 'portal'
  if (h === 'cliente.ifparquitetura.com') return 'cliente'
  if (h === 'apresentacao.ifparquitetura.com') return 'apresentacao'
  if (h === 'proposta.ifparquitetura.com') return 'proposta'
  return 'full'
}

// Sobrescreve <title> e <meta name=robots> dinamicamente por host (portal e
// cliente não devem ser indexados pelo Google)
function useHostMeta(hostMode: HostMode) {
  React.useEffect(() => {
    if (typeof document === 'undefined') return
    const titles: Record<HostMode, string> = {
      root: 'IFP Arquitetura · Studio Criativo em Santa Catarina',
      portal: 'IFP Portal — Área Restrita',
      cliente: 'Portal do Cliente — IFP Arquitetura',
      apresentacao: 'IFP Arquitetura',
      proposta: 'IFP Arquitetura',
      full: 'IFP Arquitetura',
    }
    document.title = titles[hostMode]

    if (hostMode === 'portal' || hostMode === 'cliente' || hostMode === 'apresentacao' || hostMode === 'proposta') {
      let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
      if (!robots) {
        robots = document.createElement('meta')
        robots.name = 'robots'
        document.head.appendChild(robots)
      }
      robots.content = 'noindex, nofollow'
    }
  }, [hostMode])
}

function SubdomainRedirect({ subdomain }: { subdomain: string }) {
  React.useEffect(() => {
    const { pathname, search, hash } = window.location
    window.location.replace(`https://${subdomain}.ifparquitetura.com${pathname}${search}${hash}`)
  }, [subdomain])
  return <Spinner />
}

// Rotas para o site institucional no domínio raiz
function RootSiteRoutes() {
  return (
    <Routes>
      <Route path="/" element={<SiteHome />} />
      <Route path="/site" element={<Navigate to="/" replace />} />

      {/* Contato */}
      <Route path="/contato" element={<Contato />} />

      {/* Landing pages SEO por cidade */}
      <Route path="/arquiteto-em-joinville" element={<LandingCidade slug="arquiteto-em-joinville" />} />
      <Route path="/arquiteto-em-penha-sc" element={<LandingCidade slug="arquiteto-em-penha-sc" />} />
      <Route path="/arquiteto-em-balneario-camboriu" element={<LandingCidade slug="arquiteto-em-balneario-camboriu" />} />

      {/* Páginas individuais de projeto (SEO por projeto) */}
      <Route path="/projetos/residencia-tn" element={<ProjetoPagina slug="residencia-tn" />} />
      <Route path="/projetos/residencia-js" element={<ProjetoPagina slug="residencia-js" />} />
      <Route path="/projetos/residencia-jso" element={<ProjetoPagina slug="residencia-jso" />} />
      <Route path="/projetos/comercial-ararangua-237" element={<ProjetoPagina slug="comercial-ararangua-237" />} />
      <Route path="/projetos/reserva-america" element={<ProjetoPagina slug="reserva-america" />} />
      <Route path="/projetos/business-center-1171" element={<ProjetoPagina slug="business-center-1171" />} />
      <Route path="/projetos/maroka-beach-club" element={<ProjetoPagina slug="maroka-beach-club" />} />

      {/* Portfólio Restrito (senha simples, noindex, fora da nav e do sitemap) */}
      <Route path="/portfolio-restrito" element={<PortfolioRestrito />} />
      <Route path="/portfolio-restrito/serra-alta" element={<PortfolioSerraAlta />} />

      {/* Branding / identidade visual (público, SEO) */}
      <Route path="/branding" element={<Branding />} />

      {/* Artigos (blog) */}
      <Route path="/artigos" element={<ArtigosIndex />} />
      <Route path="/artigos/arquitetura-bim" element={<ArtigoBIM />} />
      <Route path="/artigos/projeto-de-instalacoes-de-interiores" element={<ArtigoInstalacoes />} />

      {/* Landings secretas (ainda servidas no raiz por backward compat) */}
      <Route path="/apresentacao/jic" element={<LandingYachtClub />} />
      <Route path="/apresentacao/bdv" element={<LandingBaciaVovo />} />
      <Route path="/apresentacao/bdv/masterplan" element={<BdvMasterplan />} />
      <Route path="/apresentacao/magnifique" element={<LandingMagnifique />} />
      <Route path="/apresentacao/tn03-arp-residence" element={<LandingTN03 />} />

      {/* Links públicos */}
      <Route path="/p/proposta/:token" element={<PublicPropostaView />} />
      <Route path="/p/revisao/:token" element={<PublicRevisaoView />} />
      <Route path="/p/apresentacao/:token" element={<PublicApresentacaoView />} />
      <Route path="/p/ata/:token" element={<PublicAtaView />} />
      <Route path="/p/:token" element={<PublicProjetoView />} />

      {/* Cadastro público */}
      <Route path="/cadastro" element={<CadastroLead />} />
      <Route path="/cadastro/confirmacao" element={<CadastroConfirmacao />} />
      <Route path="/atualizar" element={<AtualizarCadastro />} />
      <Route path="/bem-vindo" element={<BemVindo />} />
      <Route path="/checklist" element={<ChecklistPublico />} />
      <Route path="/aceitar-convite" element={<AceitarConvite />} />

      {/* Redirects legacy → subdomínio apropriado */}
      <Route path="/login"          element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/hoje"           element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/projetos/*"     element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/clientes"       element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/tarefas"        element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/agenda"         element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/reunioes"       element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/notas"          element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/entregas"       element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/apps/*"         element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/financeiro"     element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/configuracoes"  element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/showroom/*"     element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/compartilhar"   element={<SubdomainRedirect subdomain="portal" />} />
      <Route path="/cliente/*"      element={<SubdomainRedirect subdomain="cliente" />} />

      <Route path="*" element={<SiteNotFound />} />
    </Routes>
  )
}

// Rotas exclusivas do portal do cliente no subdomínio dedicado
function ClienteSubdomainRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/cliente/login" replace />} />
      <Route path="/login" element={<Navigate to="/cliente/login" replace />} />
      <Route path="/cliente/login" element={<LoginCliente />} />
      <Route path="/cliente/definir-senha" element={<DefinirSenhaCliente />} />
      <Route path="/cliente/dashboard" element={<ClienteRoute><DashboardCliente /></ClienteRoute>} />
      <Route path="/cliente/projeto/:id" element={<ClienteRoute><ProjetoCliente /></ClienteRoute>} />
      <Route path="/cliente/projeto/:projetoId/revisao/:revisaoId/apresentacao" element={<ClienteRoute><ApresentacaoCliente /></ClienteRoute>} />
      <Route path="*" element={<Navigate to="/cliente/login" replace />} />
    </Routes>
  )
}

// Reseta o scroll do #root (container de scroll real do app) ao mudar de rota.
// Body tem overflow:hidden, então window.scrollTo não resolve.
function ScrollToTop() {
  const { pathname } = useLocation()
  React.useEffect(() => {
    document.getElementById('root')?.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [pathname])
  return null
}

// ─── Rotas ────────────────────────────────────────────────────────────────────
function AppRoutes() {
  const hostMode = getHostMode()
  useHostMeta(hostMode)

  if (hostMode === 'root') {
    return <Suspense fallback={<Spinner />}><RootSiteRoutes /></Suspense>
  }
  if (hostMode === 'cliente') {
    return <Suspense fallback={<Spinner />}><ClienteSubdomainRoutes /></Suspense>
  }

  // portal + full: árvore completa (full inclui localhost/preview/subdomínios externos)
  return (
    <Suspense fallback={<Spinner />}>
    <Routes>
      {/* Login escritório */}
      <Route path="/login" element={<Login />} />

      {/* Landing secreta — sem auth */}
      <Route path="/apresentacao/jic" element={<LandingYachtClub />} />
      <Route path="/apresentacao/bdv" element={<LandingBaciaVovo />} />
      <Route path="/apresentacao/bdv/masterplan" element={<BdvMasterplan />} />
      <Route path="/apresentacao/magnifique" element={<LandingMagnifique />} />
      <Route path="/apresentacao/tn03-arp-residence" element={<LandingTN03 />} />
      <Route path="/site" element={<SiteHome />} />

      {/* Redirects para cliente.* (em dev/full também vale, mas não é ativado porque o host check fez fallback) */}
      {hostMode === 'portal' && <Route path="/cliente/*" element={<SubdomainRedirect subdomain="cliente" />} />}

      {/* Share target — recebe conteúdo compartilhado pelo SO */}
      <Route path="/compartilhar" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Compartilhar />} />
      </Route>

      {/* Área restrita — escritório */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<PortalIndexOrNotFound />} />
        <Route path="/hoje"                                      element={<HojeRoute />} />
        <Route path="/projetos"                                  element={<Projetos />} />
        <Route path="/projetos/:id"                              element={<ProjetoDetalhe />} />
        <Route path="/projetos/:id/etapas/:etapaId"              element={<EtapaDetalhe />} />
        <Route path="/clientes"   element={<Clientes />} />
        <Route path="/clientes/:id" element={<ClienteDetalhe />} />
        <Route path="/tarefas"    element={<Tarefas />} />
        <Route path="/agenda"     element={<Agenda />} />
        <Route path="/reunioes"   element={<Navigate to="/agenda" replace />} />
        <Route path="/notas"      element={<Notas />} />
        <Route path="/entregas"   element={<Placeholder titulo="Entregas" />} />
        <Route path="/apps"                                    element={<AppsHome />} />
        <Route path="/apps/propostas"                          element={<Propostas />} />
        <Route path="/apps/propostas/nova"                     element={<PropostaBuilder />} />
        <Route path="/apps/propostas/:id"                      element={<PropostaViewer />} />
        <Route path="/apps/propostas/:id/editar"               element={<PropostaBuilder />} />
        <Route path="/apps/catalogos"                           element={<Catalogos />} />
        <Route path="/financeiro" element={<PermissionRoute modulo="financeiro"><Financeiro /></PermissionRoute>} />
        <Route path="/admin"          element={<Placeholder titulo="Admin" />} />
        <Route path="/configuracoes" element={<PermissionRoute modulo="configuracoes"><Configuracoes /></PermissionRoute>} />
      </Route>

      {/* Showroom — apresentação para leads (sem AppLayout) */}
      <Route path="/showroom" element={<ShowroomRoute><Showroom /></ShowroomRoute>} />
      <Route path="/showroom/projeto/:id" element={<ShowroomRoute><ProjetoCliente showroomMode={true} /></ShowroomRoute>} />

      {/* Preview proposta — full-page landing (sem AppLayout).
          Rota não-protegida de propósito: PropostaPreview trata auth internamente
          (escritório vê o preview; visitante sem login recebe interstitial com o
          link público, em vez de cair na tela de login do portal). */}
      <Route path="/apps/propostas/:id/preview" element={<PropostaPreview />} />

      {/* Preview — escritório vê como cliente (sem AppLayout) */}
      <Route path="/cliente-preview/:clienteId" element={
        <ProtectedRoute><PreviewDashboardWrapper /></ProtectedRoute>
      } />
      <Route path="/cliente-preview/:clienteId/projeto/:id" element={
        <ProtectedRoute><PreviewProjetoWrapper /></ProtectedRoute>
      } />

      {/* Área do cliente */}
      <Route path="/cliente/login"    element={<LoginCliente />} />
      <Route path="/cliente/definir-senha" element={<DefinirSenhaCliente />} />
      <Route path="/cliente/dashboard" element={<ClienteRoute><DashboardCliente /></ClienteRoute>} />
      <Route path="/cliente/projeto/:id" element={<ClienteRoute><ProjetoCliente /></ClienteRoute>} />
      <Route path="/cliente/projeto/:projetoId/revisao/:revisaoId/apresentacao" element={<ClienteRoute><ApresentacaoCliente /></ClienteRoute>} />

      {/* Links públicos — sem autenticação */}
      <Route path="/p/proposta/:token" element={<PublicPropostaView />} />
      <Route path="/p/revisao/:token" element={<PublicRevisaoView />} />
      <Route path="/p/apresentacao/:token" element={<PublicApresentacaoView />} />
      <Route path="/p/ata/:token" element={<PublicAtaView />} />
      <Route path="/p/:token" element={<PublicProjetoView />} />

      {/* Cadastro público de leads */}
      <Route path="/cadastro" element={<CadastroLead />} />
      <Route path="/cadastro/confirmacao" element={<CadastroConfirmacao />} />
      <Route path="/atualizar" element={<AtualizarCadastro />} />
      <Route path="/bem-vindo" element={<BemVindo />} />
      <Route path="/checklist" element={<ChecklistPublico />} />
      <Route path="/aceitar-convite" element={<AceitarConvite />} />

      <Route path="*" element={<PortalIndexOrNotFound />} />
    </Routes>
    </Suspense>
  )
}

// ─── Meeting overlay (persiste na navegação) ──────────────────────────────────
function MeetingOverlay() {
  const { meeting, mode, displayMode } = useMeeting()
  if (!meeting) return null
  return (
    <>
      <BoasVindas />
      {mode === 'live' && displayMode === 'fullscreen' && <ReuniaoAoVivo />}
      {mode === 'live' && displayMode === 'compact'    && <MeetingCompact />}
      {mode === 'live' && displayMode === 'capsule'    && <MeetingBar />}
    </>
  )
}

// ─── Captura overlay (persiste na navegação) ──────────────────────────────────
function CapturaOverlay() {
  const { sessao } = useSessaoCapturaCtx()
  if (!sessao) return null
  return (
    <>
      <SessaoCaptura />
      <CapturaBar />
    </>
  )
}

// ─── App root ─────────────────────────────────────────────────────────────────
function SentryFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-8">
      <div className="max-w-md text-center">
        <h1 className="mb-4 text-xl font-bold text-[#f0f0f0]">Algo deu errado</h1>
        <p className="mb-6 text-sm text-[#999]">
          O erro foi registrado automaticamente. Tente recarregar a página.
        </p>
        <button
          onClick={resetError}
          className="rounded-full bg-hero px-6 py-2 text-sm font-bold text-[#090909]"
        >
          Recarregar
        </button>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback} showDialog={false}>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <EscritorioProvider>
          <ClienteAuthProvider>
            <TooltipProvider>
              <MeetingProvider>
                <SessaoCapturaProvider>
                  <TimeTrackerProvider>
                    <AppRoutes />
                    <MeetingOverlay />
                    <CapturaOverlay />
                  </TimeTrackerProvider>
                </SessaoCapturaProvider>
                <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.10)', color: '#f0f0f0', fontFamily: 'Inter,sans-serif', fontSize: 12 } }} />
              </MeetingProvider>
            </TooltipProvider>
          </ClienteAuthProvider>
          </EscritorioProvider>
        </BrowserRouter>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  )
}
